import { Loader2, Pause, Play } from "lucide-react";
import { GLOW } from "@/lib/glass";

const LABEL = {
  idle: "Listen to The Crossing",
  preparing: "Summoning the voice…",
  playing: "Pause the voice",
  paused: "Resume the voice",
  error: "The voice is silent — try again",
};

function clock(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export default function CrossingNarrator({ narration }) {
  const { state, time, duration, toggle, seek, audioProps } = narration;
  const engaged = state === "playing" || state === "paused";
  const progress = duration ? (time / duration) * 100 : 0;
  const Icon = state === "preparing" ? Loader2 : state === "playing" ? Pause : Play;

  const scrub = (e) => {
    if (!engaged || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    seek(((e.clientX - rect.left) / rect.width) * duration);
  };

  return (
    <div
      data-testid="crossing-narrator"
      className={`${engaged ? "sticky top-14 z-20 -mx-4 rounded-b-lg bg-black/80 px-4 backdrop-blur-md" : ""} mb-10 flex items-center gap-5 py-2 sm:mb-14`}
    >
      <button
        type="button"
        data-testid="crossing-narrator-toggle"
        onClick={toggle}
        disabled={state === "preparing"}
        aria-pressed={state === "playing"}
        className="relative inline-flex items-center gap-3 overflow-hidden rounded-full border border-[#84DFF4]/40 bg-black/60 px-6 py-3 text-xs uppercase tracking-[0.18em] text-[#84DFF4] backdrop-blur-md transition-[background-color,color,border-color] duration-300 hover:border-[#84DFF4] hover:bg-[#84DFF4] hover:text-black disabled:cursor-wait disabled:opacity-70 disabled:hover:bg-black/60 disabled:hover:text-[#84DFF4]"
        style={{ textShadow: state === "playing" ? "none" : GLOW.gold }}
      >
        <div
          data-testid="crossing-narrator-progress"
          className="absolute bottom-0 left-0 h-[1px] bg-[#84DFF4] transition-all duration-100 ease-linear"
          style={{ width: `${progress}%` }}
        />
        <Icon size={14} strokeWidth={1.5} className={state === "preparing" ? "animate-spin" : ""} />
        <span data-testid="crossing-narrator-label">{LABEL[state]}</span>
      </button>

      {engaged && duration > 0 && (
        <div
          data-testid="crossing-narrator-timeline"
          role="slider"
          aria-label="Narration position"
          aria-valuemin={0}
          aria-valuemax={Math.round(duration)}
          aria-valuenow={Math.round(time)}
          onClick={scrub}
          className="group flex min-w-0 flex-1 cursor-pointer items-center gap-4 py-3"
        >
          <div className="relative h-px flex-1 bg-[#F5F5F7]/15">
            <div
              className="absolute inset-y-0 left-0 bg-[#84DFF4]"
              style={{ width: `${progress}%`, boxShadow: GLOW.gold }}
            />
            <div
              className="absolute top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#84DFF4] opacity-0 transition-opacity duration-300 group-hover:opacity-100"
              style={{ left: `${progress}%` }}
            />
          </div>
          <span
            data-testid="crossing-narrator-clock"
            className="shrink-0 font-mono text-[0.65rem] tabular-nums tracking-[0.12em] text-[#F5F5F7]/50"
          >
            {clock(time)} / {clock(duration)}
          </span>
        </div>
      )}

      <audio data-testid="crossing-narrator-audio" {...audioProps} />
    </div>
  );
}
